import React, { useState, useEffect } from "react";
import { Modal, Spinner, Alert, Badge, ProgressBar } from "react-bootstrap";
import { selectToken } from "../auth/auth.selector";
import { useSelector, useDispatch } from "react-redux";
import { formatDateTime, formatTicketDate } from "../common-data/Converts";
import "../../styles/ticket-system/ticket-lifecycle-timeline.css";
import { fetchApiData } from "../../redux/reducers/commonApiSlice";

const TicketLifecycleTimeline = ({ show, onHide, ticketId, ticketInfo }) => {
  const [lifecycleData, setLifecycleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = useSelector(selectToken);
  const dispatch = useDispatch();

  useEffect(() => {
    if (ticketId) {
      fetchLifecycleData();
    }
  }, [show, ticketId]);

  const fetchLifecycleData = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await dispatch(
        fetchApiData({
          method: "GET",
          endpoint: `/ticketSystem/lifecycle?ticket_id=${ticketId}`,
          token,
          payload: {
            filters: {},
          },
        })
      );

      if (result?.payload?.status) {
        setLifecycleData(result.payload.data);
      } else {
        setError(result?.payload?.message || "Failed to fetch lifecycle data");
      }
    } catch (err) {
      console.error("Error fetching lifecycle data:", err);
      setError("Failed to load ticket lifecycle");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "done":
      case "completed":
        return "success";
      case "pending":
      case "in_progress":
        return "warning";
      case "rejected":
      case "failed":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getSLABadgeVariant = (slaStatus) => {
    switch (slaStatus?.toLowerCase()) {
      case "within sla":
        return "success";
      case "sla breached":
        return "danger";
      case "pending":
        return "warning";
      default:
        return "secondary";
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType?.toLowerCase()) {
      case "ticket_raised":
        return "📋";
      case "assigned":
        return "👤";
      case "work_started":
        return "🔧";
      case "work_completed":
        return "✅";
      case "resolved":
        return "🎯";
      case "verified":
        return "🔍";
      case "rejected":
        return "❌";
      case "reopened":
        return "🔄";
      default:
        return "📝";
    }
  };

  // Custom HTML sanitization function
  const sanitizeHtml = (html) => {
    if (!html || typeof html !== "string") {
      return "";
    }

    // Define allowed tags and attributes
    const allowedTags = [
      "b",
      "i",
      "em",
      "strong",
      "u",
      "br",
      "p",
      "div",
      "span",
    ];
    const allowedAttributes = ["class", "style"];

    // Remove script tags and their content
    let sanitized = html.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      ""
    );

    // Remove any tags that contain javascript: or data: protocols
    sanitized = sanitized.replace(
      /<[^>]*\s+(?:href|src|action)=["']?(?:javascript|data):[^"'>]*["']?[^>]*>/gi,
      ""
    );

    // Remove any event handlers
    sanitized = sanitized.replace(/<[^>]*\s+on\w+=["'][^"']*["'][^>]*>/gi, "");

    // Remove any style attributes that contain javascript
    sanitized = sanitized.replace(
      /<[^>]*\s+style=["'][^"']*javascript:[^"']*["'][^>]*>/gi,
      ""
    );

    // Remove any class attributes that contain javascript
    sanitized = sanitized.replace(
      /<[^>]*\s+class=["'][^"']*javascript[^"']*["'][^>]*>/gi,
      ""
    );

    // Only allow specific tags
    const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
    sanitized = sanitized.replace(tagRegex, (match, tagName) => {
      const lowerTagName = tagName.toLowerCase();

      // If it's not an allowed tag, remove it but keep the content
      if (!allowedTags.includes(lowerTagName)) {
        return "";
      }

      // For allowed tags, clean up attributes
      if (match.startsWith("</")) {
        // Closing tag - just return it as is
        return `</${lowerTagName}>`;
      } else {
        // Opening tag - clean up attributes
        let cleanTag = `<${lowerTagName}`;

        // Extract and clean attributes
        const attrRegex = /(\w+)=["']([^"']*)["']/g;
        let attrMatch;
        while ((attrMatch = attrRegex.exec(match)) !== null) {
          const attrName = attrMatch[1].toLowerCase();
          const attrValue = attrMatch[2];

          // Only allow specific attributes
          if (allowedAttributes.includes(attrName)) {
            // Additional check for style attribute to prevent javascript
            if (
              attrName === "style" &&
              attrValue.toLowerCase().includes("javascript")
            ) {
              continue;
            }
            cleanTag += ` ${attrName}="${attrValue}"`;
          }
        }

        cleanTag += ">";
        return cleanTag;
      }
    });

    return sanitized;
  };

  // Safe Notes Component with expand/collapse functionality
  const SafeNotes = ({ notes, maxLength = 100 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!notes || notes.trim() === "") {
      return <span className="text-muted">—</span>;
    }

    // Sanitize the HTML content using custom function
    const sanitizedHtml = sanitizeHtml(notes);

    // Check if content needs truncation
    const textContent = notes.replace(/<[^>]*>/g, ""); // Remove HTML tags for length check
    const needsTruncation = textContent.length > maxLength;

    if (needsTruncation) {
      // Truncate the text content and add ellipsis
      const truncatedText = textContent.substring(0, maxLength) + "...";

      return (
        <div className="notes-content">
          <span
            dangerouslySetInnerHTML={{
              __html: isExpanded ? sanitizedHtml : sanitizeHtml(truncatedText),
            }}
            className="notes-text"
          />
          <button
            className="btn btn-link btn-sm p-0 ms-1"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? "Show less" : "View full notes"}
          >
            {isExpanded ? "Show less" : "Read more"}
          </button>
        </div>
      );
    }

    return (
      <div className="notes-content">
        <span
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          className="notes-text"
        />
      </div>
    );
  };

  // Safe HTML rendering function for ticket notes
  const renderSafeNotes = (notes, maxLength = 100) => {
    return <SafeNotes notes={notes} maxLength={maxLength} />;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="ticket-lifecycle-modal"
      // style={{ maxWidth: "95vw" }}
    >
      <Modal.Header closeButton className="lifecycle-modal-header">
        <Modal.Title>
          <div className="d-flex align-items-center">
            <span className="timeline-icon me-2">📊</span>
            <div>
              <h5 className="mb-0">Ticket Lifecycle Timeline</h5>
              <div className="ticket-header-info">
                <div className="ticket-meta">
                  <span className="ticket-number">
                    {ticketInfo?.ticket_no || `Ticket #${ticketId}`}
                  </span>
                  {ticketInfo?.gym_name && (
                    <span className="ticket-gym">{ticketInfo.gym_name}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="lifecycle-modal-body">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading ticket lifecycle...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">
            <Alert.Heading>Error Loading Timeline</Alert.Heading>
            <p>{error}</p>
          </Alert>
        ) : lifecycleData ? (
          <div className="lifecycle-container">
            {/* Ticket Summary Section */}
            {/* <div className="ticket-summary-section">
              <div className="summary-header">
                <h6 className="summary-title">Ticket Overview</h6>
              </div>
              <div className="summary-content">
                <div className="row">
                  <div className="col-md-6">
                    <div className="summary-item">
                      <span className="summary-label">Issue:</span>
                      <span className="summary-value">
                        {lifecycleData.ticket_info?.category_name} →{" "}
                        {lifecycleData.ticket_info?.sub_category_name}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Gym:</span>
                      <span className="summary-value">
                        {lifecycleData.ticket_info?.gym_name}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Priority:</span>
                      <Badge
                        bg={
                          lifecycleData.ticket_info?.priority === "high"
                            ? "danger"
                            : lifecycleData.ticket_info?.priority === "medium"
                            ? "warning"
                            : "success"
                        }
                        className="priority-badge"
                      >
                        {lifecycleData.ticket_info?.priority?.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="summary-item">
                      <span className="summary-label">Current Status:</span>
                      <Badge
                        bg={getStatusBadgeVariant(
                          lifecycleData.ticket_info?.status
                        )}
                        className="status-badge"
                      >
                        {lifecycleData.ticket_info?.status
                          ?.replace(/_/g, " ")
                          .toUpperCase()}
                      </Badge>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total TAT:</span>
                      <span className="summary-value">
                        {formatTimeTaken(
                          lifecycleData.timeline_summary?.total_tat
                        ) || "—"}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Progress:</span>
                      <div className="progress-wrapper">
                        <ProgressBar
                          now={
                            lifecycleData.timeline_summary
                              ?.progress_percentage || 0
                          }
                          variant="primary"
                          className="progress-bar-custom"
                        />
                        <small className="progress-text">
                          {lifecycleData.timeline_summary?.completed_stages ||
                            0}{" "}
                          of {lifecycleData.timeline_summary?.total_stages || 0}{" "}
                          stages
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}

            {/* SLA Summary Section */}
            {/* {lifecycleData.sla_summary && (
              <div className="sla-summary-section">
                <div className="summary-header">
                  <h6 className="summary-title">SLA Summary</h6>
                </div>
                <div className="sla-summary-content">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="sla-stat">
                        <span className="sla-stat-label">Overall Status</span>
                        <Badge
                          bg={getSLABadgeVariant(
                            lifecycleData.sla_summary.overall_status
                          )}
                          className="sla-overall-badge"
                        >
                          {lifecycleData.sla_summary.overall_status}
                        </Badge>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="sla-stat">
                        <span className="sla-stat-label">Within SLA</span>
                        <span className="sla-stat-value compliant">
                          {lifecycleData.sla_summary.compliant_count || 0}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="sla-stat">
                        <span className="sla-stat-label">SLA Breached</span>
                        <span className="sla-stat-value breached">
                          {lifecycleData.sla_summary.breach_count || 0}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="sla-stat">
                        <span className="sla-stat-label">Pending</span>
                        <span className="sla-stat-value pending">
                          {lifecycleData.sla_summary.pending_count || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )} */}

            {/* Timeline Table Section */}
            <div className="timeline-section">
              {/* <div className="timeline-header">
                <h6 className="timeline-title">Lifecycle Timeline</h6>
              </div> */}
              <div className="timeline-table-container">
                <div className="table-responsive">
                  <table className="timeline-table">
                    <thead>
                      <tr>
                        <th>Step (Action)</th>
                        <th>Owner</th>
                        <th>Role</th>
                        <th>Ticket LifeCycle</th>
                        <th>Status</th>
                        <th>SLA Status</th>
                        <th>Time Taken</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lifecycleData.lifecycle_stages?.map((stage, index) => (
                        <tr
                          key={index}
                          className={
                            stage.status?.toLowerCase() === "pending"
                              ? "pending-row"
                              : ""
                          }
                        >
                          <td className="step-cell">
                            <div className="step-content">
                              <span className="step-icon">
                                {getActionIcon(stage.action_type)}
                              </span>
                              <span className="step-name">{stage.step}</span>
                            </div>
                          </td>
                          <td className="owner-cell">{stage.owner || "—"}</td>
                          <td className="role-cell">
                            {stage.owner_role || "—"}
                          </td>
                          <td className="timestamp-cell">
                            {stage.timestamp
                              ? formatDateTime(stage.timestamp)
                              : "—"}
                          </td>
                          <td className="status-cell">
                            <Badge
                              bg={getStatusBadgeVariant(stage.status)}
                              className="table-status-badge"
                            >
                              {stage.status || "—"}
                            </Badge>
                          </td>
                          <td className="sla-cell">
                            {stage.sla_status &&
                            stage.sla_status !== "Immediate" ? (
                              <div className="sla-info">
                                <Badge
                                  bg={getSLABadgeVariant(stage.sla_status)}
                                  className="table-sla-badge"
                                >
                                  {stage.sla_status}
                                </Badge>
                                {stage?.sla_allowed && (
                                  <div className="sla-time">
                                    <small className="text-muted">
                                      Allowed Time - {stage?.sla_allowed}
                                    </small>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="time-taken-cell">
                            {/* {formatTimeTaken(stage?.time_taken)} */}
                            {stage?.sla_actual}
                          </td>
                          <td className="notes-cell">
                            {renderSafeNotes(stage?.notes, 50)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Alert variant="info">
            No lifecycle data available for this ticket.
          </Alert>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TicketLifecycleTimeline;
